import { useEffect, useRef } from 'react'
import { createConsumer } from '@rails/actioncable'
import { router, usePage } from '@inertiajs/react'
import { useToast } from '@/hooks/useToast'
import { isRecomputeExpected } from '@/lib/expectedRecompute'

const consumer = createConsumer()

interface NotificationData {
  event: string
  message: string
  changed?: number
  pushed?: number
  failed?: number
  at: string
}

export function usePropertyNotifications(propertyId: number) {
  const { addToast } = useToast()
  const { url } = usePage()
  const propertyIdRef = useRef(propertyId)
  const urlRef = useRef(url)
  propertyIdRef.current = propertyId
  urlRef.current = url

  useEffect(() => {
    const subscription = consumer.subscriptions.create(
      { channel: 'PropertyNotificationsChannel', property_id: propertyId },
      {
        received(data: unknown) {
          const notification = data as NotificationData
          if (propertyIdRef.current !== propertyId) return

          const isError =
            notification.event === 'computation_error' || notification.event === 'push_error'
          // Only recomputes change the rates the user is reading. Pushes only change
          // PMS sync state, which is reflected separately via crown:sync-status-updated.
          const isRatesChanged = notification.event === 'rates_computed'
          const isLifecycle =
            notification.event.endsWith('_enqueued') || notification.event.endsWith('_started')

          // Always update header pipeline widgets instantly
          window.dispatchEvent(
            new CustomEvent('crown:sync-status-updated', {
              detail: {
                event: notification.event,
                at: notification.at,
                pushed: notification.pushed,
              },
            })
          )

          // Only show toasts for completion/error events (not enqueued/started lifecycle)
          if (!isLifecycle && notification.message) {
            addToast({
              title: notification.message,
              variant: isError ? 'destructive' : 'success',
              persistent: isError,
            })
          }

          // When the displayed rates change: reload non-calendar pages, or show the
          // calendar banner so the user can refresh on their own. If the recompute
          // was triggered by an action the user just performed in this tab, skip
          // the banner and silently reload — they don't need to be asked to refresh
          // for a change they initiated.
          if (isRatesChanged) {
            const currentUrl = urlRef.current
            const isCalendarPage =
              currentUrl.includes('/calendar/') || currentUrl.endsWith('/calendar')

            if (isRecomputeExpected()) {
              router.reload()
            } else if (isCalendarPage) {
              window.dispatchEvent(
                new CustomEvent('crown:rates-updated', { detail: notification })
              )
            } else {
              router.reload()
            }
          }
        },
      }
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [propertyId, addToast])
}
